import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import Swal from "sweetalert2/dist/sweetalert2.js";
import { api } from './api';
import { SelectionTree, FolderPicker } from './Tree';
import Review from './Review';

const STEPS = [
    { key: "origen", label: "Qué copiar" },
    { key: "destino", label: "A dónde" },
    { key: "revision", label: "Revisión" },
    { key: "ejecutar", label: "Ejecutar" },
];

const tenantOption = (t) => ({ value: t.id, label: `${t.name}${t.active ? "" : " (inactiva)"}`, tenant: t });

// El asistente: elegir qué copiar, a dónde, revisar el plan (decisiones, catálogos, avisos) y
// lanzar un ensayo o la copia real. El plan se arma en el servidor y no escribe nada.
const Wizard = ({ onLaunched }) => {
    const [step, setStep] = useState(0);
    const [tenants, setTenants] = useState([]);
    const [error, setError] = useState(null);

    const [source, setSource] = useState(null);
    const [sourceTree, setSourceTree] = useState(null);
    const [cats, setCats] = useState([]);
    const [fmts, setFmts] = useState([]);

    const [target, setTarget] = useState(null);
    const [targetTree, setTargetTree] = useState(null);
    const [folder, setFolder] = useState(null);
    const [users, setUsers] = useState([]);
    const [owner, setOwner] = useState(null);
    const [createCatalogs, setCreateCatalogs] = useState(true);
    // Dónde caen los formatos de OTROS módulos que la copia necesita.
    const [carpetas, setCarpetas] = useState("raiz");

    const [plan, setPlan] = useState(null);
    const [planning, setPlanning] = useState(false);
    const [decisions, setDecisions] = useState({});
    const [dirty, setDirty] = useState(false);
    const [launching, setLaunching] = useState(false);

    useEffect(() => {
        api.tenants().then((r) => (r.ok ? setTenants(r.tenants) : setError(r.error)));
    }, []);

    const pickSource = (opt) => {
        setSource(opt); setSourceTree(null); setCats([]); setFmts([]); setPlan(null);
        if (opt) api.tree(opt.value).then((r) => (r.ok ? setSourceTree(r) : setError(r.error)));
    };

    const pickTarget = (opt) => {
        setTarget(opt); setTargetTree(null); setFolder(null); setUsers([]); setOwner(null); setPlan(null);
        if (!opt) return;
        api.tree(opt.value).then((r) => (r.ok ? setTargetTree(r) : setError(r.error)));
        api.users(opt.value).then((r) => r.ok && setUsers(r.users));
    };

    const payload = (extra = {}) => ({
        source_tenant_id: source && source.value,
        target_tenant_id: target && target.value,
        format_category_ids: cats,
        format_ids: fmts,
        decisions,
        backup_owner_id: owner ? owner.value : undefined,
        target_folder_id: folder,
        create_catalogs: createCatalogs,
        carpetas_externas: carpetas,
        ...extra,
    });

    const runPlan = (decs = decisions) => {
        setPlanning(true); setError(null);
        api.plan({ ...payload(), decisions: decs }).then((r) => {
            setPlanning(false);
            if (!r.ok) return setError(r.error);
            setPlan(r.plan); setDirty(false);
        });
    };

    const goto = (i) => {
        setError(null);
        if (i === 2 && (!plan || dirty)) runPlan();
        setStep(i);
    };

    const changeDecision = (id, choice) => {
        setDecisions({ ...decisions, [id]: choice });
        setDirty(true);
    };

    const launch = (dryRun) => {
        const go = () => {
            setLaunching(true); setError(null);
            api.create(payload({ dry_run: dryRun })).then((r) => {
                setLaunching(false);
                if (!r.ok) return setError(r.error);
                onLaunched(r.execution.id);
            });
        };
        if (dryRun) return go();
        Swal.fire({
            title: "¿Copiar de verdad?",
            html: `Se van a crear <b>${plan ? plan.total : ""}</b> elementos (preguntas, permisos, PDFs, fórmulas…) en <b>${target.label}</b>. La copia se verifica antes de guardarse y después se puede deshacer desde el historial.`,
            type: "question", showCancelButton: true, confirmButtonText: "Sí, copiar", cancelButtonText: "Cancelar",
            confirmButtonColor: "#0061fe",
        }).then((res) => { if (res.value) go(); });
    };

    const canNext = [
        !!source && (cats.length + fmts.length > 0),
        !!target,
        !!plan && !dirty && !plan.bloqueado && !planning,
        true,
    ];
    const sameTenant = source && target && source.value === target.value;
    const options = tenants.map(tenantOption);

    return (
        <div>
            <div className="sd-steps">
                {STEPS.map((s, i) => (
                    <div key={s.key} className={`sd-step ${i === step ? "is-active" : ""} ${i < step ? "is-done" : ""}`}
                        onClick={() => i < step && goto(i)}>
                        <span className="sd-step__num">{i < step ? <i className="fas fa-check"></i> : i + 1}</span>
                        {s.label}
                    </div>
                ))}
            </div>

            {error && <div className="rk-alert" style={{ marginBottom: 14 }}><i className="fas fa-exclamation-circle"></i> {error}</div>}

            {step === 0 && (
                <div className="sd-card">
                    <h3 className="sd-card__title"><i className="fas fa-box-open"></i> Qué copiar</h3>
                    <p className="sd-card__hint">Elige la empresa de origen y marca módulos (se copian con todo lo que tienen dentro) o formatos sueltos. Lo que esos formatos necesiten de otros módulos —subformularios, campos tipo formato— se resuelve en la revisión.</p>
                    <div className="rk-field">
                        <label className="rk-label">Empresa de origen</label>
                        <Select classNamePrefix="select" options={options} value={source} onChange={pickSource}
                            placeholder="Buscar empresa…" isClearable />
                    </div>
                    {source && !sourceTree && <p className="sd-muted"><i className="fas fa-spinner fa-spin"></i> Cargando módulos…</p>}
                    {sourceTree && (
                        <div className="rk-field">
                            <label className="rk-label">
                                Módulos y formatos · <span className="sd-muted">{cats.length} módulo(s), {fmts.length} formato(s) marcados</span>
                            </label>
                            <SelectionTree categories={sourceTree.categories} formats={sourceTree.formats}
                                selectedCategories={cats} selectedFormats={fmts}
                                onChange={(c, f) => { setCats(c); setFmts(f); setPlan(null); }} />
                        </div>
                    )}
                </div>
            )}

            {step === 1 && (
                <div className="sd-card">
                    <h3 className="sd-card__title"><i className="fas fa-map-marker-alt"></i> A dónde</h3>
                    <p className="sd-card__hint">La empresa destino puede ser la misma (se crea una copia al lado) u otra. En otra empresa, cargos y procesos se emparejan por nombre y los que falten se crean.</p>
                    <div className="row">
                        <div className="col-md-6">
                            <div className="rk-field">
                                <label className="rk-label">Empresa destino</label>
                                <Select classNamePrefix="select" options={options} value={target} onChange={pickTarget}
                                    placeholder="Buscar empresa…" isClearable />
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="rk-field">
                                <label className="rk-label">Dueño de respaldo <span className="sd-muted">(opcional)</span></label>
                                <Select classNamePrefix="select" isClearable isDisabled={!target}
                                    options={users.map((u) => ({ value: u.id, label: `${u.name} · ${u.email}` }))}
                                    value={owner} onChange={(o) => { setOwner(o); setPlan(null); }}
                                    placeholder="Automático (un administrador del destino)" />
                            </div>
                        </div>
                    </div>
                    {target && sameTenant && (
                        <p className="sd-muted"><i className="fas fa-info-circle"></i> Misma empresa: la copia se crea al lado del original, con nombres «(Copia N)» donde choquen. Lo externo se vincula al original.</p>
                    )}
                    {target && !sameTenant && (
                        <label className="sd-switch" style={{ marginBottom: 12 }}>
                            <input type="checkbox" checked={createCatalogs} onChange={(e) => setCreateCatalogs(e.target.checked)} />
                            Crear en el destino los cargos y procesos que no existan
                        </label>
                    )}
                    {target && (
                        <div className="rk-field">
                            <label className="rk-label">Los formatos de otros módulos que la copia necesita</label>
                            <select className="rk-input" value={carpetas}
                                onChange={(e) => { setCarpetas(e.target.value); setPlan(null); }}>
                                <option value="raiz">Traerlos dentro del módulo copiado (recomendado)</option>
                                <option value="ruta">Respetar sus carpetas del origen (se crean en el destino)</option>
                            </select>
                            <small className="sd-muted">
                                {carpetas === "raiz"
                                    ? "Quedan junto a los demás formatos de la copia, en una sola carpeta."
                                    : "Se recrean sus carpetas para que el destino quede igual que el origen. Las carpetas nuevas llegan vacías: solo traen ese formato."}
                            </small>
                        </div>
                    )}

                    {target && (
                        <div className="rk-field">
                            <label className="rk-label">Carpeta donde queda la copia</label>
                            {targetTree
                                ? <FolderPicker categories={targetTree.categories} value={folder} onChange={setFolder} />
                                : <p className="sd-muted"><i className="fas fa-spinner fa-spin"></i> Cargando carpetas…</p>}
                        </div>
                    )}
                </div>
            )}

            {step === 2 && (
                <Review plan={plan} planning={planning} dirty={dirty} decisions={decisions}
                    onChange={changeDecision} onReplan={() => runPlan()} />
            )}

            {step === 3 && plan && (
                <div className="sd-card">
                    <h3 className="sd-card__title"><i className="fas fa-play-circle"></i> Ejecutar</h3>
                    <p className="sd-card__hint">
                        <b>{plan.origen.nombre}</b> → <b>{plan.destino.nombre}</b> · {plan.total} elementos.
                        Recomendado: primero un <b>ensayo</b>. Copia todo de verdad, lo verifica y lo revierte al final: en el destino no queda nada y el log te dice exactamente qué pasaría.
                    </p>
                    <div className="sd-stats">
                        <div className="sd-stat"><div className="sd-stat__value"><i className="fas fa-flask"></i></div>
                            <div className="sd-stat__label">Ensayo: se revierte siempre. Sin riesgo.</div></div>
                        <div className="sd-stat"><div className="sd-stat__value"><i className="fas fa-bolt"></i></div>
                            <div className="sd-stat__label">Copia real: queda en el destino. Se puede deshacer.</div></div>
                    </div>
                    <div className="sd-footer">
                        <button type="button" className="rk-btn rk-btn--soft" disabled={launching} onClick={() => launch(true)}>
                            {launching ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-flask"></i>} Ensayar
                        </button>
                        <button type="button" className="rk-btn rk-btn--primary" disabled={launching} onClick={() => launch(false)}>
                            {launching ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-bolt"></i>} Copiar de verdad
                        </button>
                    </div>
                </div>
            )}

            <div className="sd-footer">
                <button type="button" className="rk-btn rk-btn--ghost" disabled={step === 0} onClick={() => goto(step - 1)}>
                    <i className="fas fa-arrow-left"></i> Atrás
                </button>
                {step < 3 && (
                    <button type="button" className="rk-btn rk-btn--primary" disabled={!canNext[step]} onClick={() => goto(step + 1)}>
                        Siguiente <i className="fas fa-arrow-right"></i>
                    </button>
                )}
            </div>
        </div>
    );
};

export default Wizard;
